import { ObjectDirective } from 'vue';
import { getMeasuredTextWidth } from '@/common/utils/browser/dom';

/**
 * input 의 폭을 입력 내용(없으면 placeholder)의 실제 렌더 너비에 맞춰 조정한다.
 * CSS `field-sizing: content` 대체 — browserslist 기준선이 field-sizing 을 지원하지 않아
 * canvas 기반 텍스트 측정(getMeasuredTextWidth)으로 동일 효과를 낸다.
 */
const CARET_BUFFER_PX = 2;

// updated 훅이 리렌더마다 호출되므로, 측정 대상 텍스트가 같으면 재측정을 건너뛴다 (WeakMap — 요소 GC를 막지 않음).
const lastMeasure = new WeakMap<HTMLInputElement, { text: string; width: string }>();

const resizeToContent = (el: HTMLInputElement) => {
  const text = el.value || el.placeholder || '';
  const cached = lastMeasure.get(el);
  // 1차 게이트: 측정 대상 텍스트가 그대로면 getComputedStyle(스타일/레이아웃 강제 재계산을
  // 유발할 수 있음) 호출 전에 조기 반환한다. updated 훅은 부모 리렌더마다 호출되고 그 대부분은
  // 텍스트 변화가 없으므로, 이 게이트로 매 렌더의 computed style 읽기까지 건너뛴다.
  // 트레이드오프: 텍스트가 같은 채 폰트/박스 스타일만 바뀌는 경우(예: 테마 전환)는 다음
  // 텍스트 변경 전까지 재측정되지 않는다.
  if (cached && cached.text === text && el.style.width === cached.width) {
    return;
  }
  const style = getComputedStyle(el);
  const textWidth = getMeasuredTextWidth(text, style.fontSize, style.fontFamily, {
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
  });
  // border-box 는 width 안에 padding·border 가 포함되므로 텍스트 폭에 더해준다.
  const extra =
    style.boxSizing === 'border-box'
      ? parseFloat(style.paddingLeft) +
        parseFloat(style.paddingRight) +
        parseFloat(style.borderLeftWidth) +
        parseFloat(style.borderRightWidth)
      : 0;
  const width = `${Math.ceil(textWidth) + extra + CARET_BUFFER_PX}px`;
  el.style.width = width;
  lastMeasure.set(el, { text, width });
};

const onInput = (event: Event) => {
  resizeToContent(event.currentTarget as HTMLInputElement);
};

const inputAutoWidth: ObjectDirective<HTMLInputElement> = {
  mounted(el) {
    resizeToContent(el);
    el.addEventListener('input', onInput);
  },
  updated(el) {
    // placeholder 등 바인딩 변경 시 폭 재계산
    resizeToContent(el);
  },
  unmounted(el) {
    el.removeEventListener('input', onInput);
  },
};

export { inputAutoWidth };
